import { AsyncPipe, TitleCasePipe } from '@angular/common';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  input,
  output,
  inject,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
  UntypedFormControl,
} from '@angular/forms';
import { AppErrors, NotFoundError } from '@fitness-force/models';
import { AssignedToComponent } from '@fitness-force/common/assigned-to';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AvatarModule } from 'primeng/avatar';
import { BlockUIModule } from 'primeng/blockui';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';

import { BehaviorSubject, Observable, of } from 'rxjs';
import { ShortNamePipe } from '@fitness-force/pipes/short-name';
import {
  InvoiceService,
  StaffStoreService,
  ToastMessageService,
} from '@fitness-force/api';
import type {
  ChangeSalesRep,
  Claim,
  Invoice,
  STAFFINTERFACE,
} from '@fitness-force/models';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { ConfirmationService } from 'primeng/api';
@Component({
  selector: 'change-bill-owner',
  templateUrl: './change-bill-owner.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SelectModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    AvatarModule,
    ButtonModule,
    RippleModule,
    BlockUIModule,
    StyleClassModule,
    InputTextModule,
    PasswordModule,
    ShortNamePipe,
    AvatarModule,
    AssignedToComponent,
    ConfirmPopupModule,
    TranslateModule,
    AsyncPipe,
    TitleCasePipe,
  ],
})
export class ChangeBillOwnerComponent implements OnInit {
  translate = inject(TranslateService);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly invoiceService = inject(InvoiceService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly staffStoreService = inject(StaffStoreService);

  readonly changeBillOwnerModal = input<boolean>(false, { alias: 'visible' });
  readonly invoiceData = input<Invoice>({} as Invoice);
  readonly staffIds$ = input<Observable<number[]>>(of([-1]), {
    alias: 'filterstaff',
  });
  readonly closeDialog = output<{ action: string }>();

  allClientData$ = new BehaviorSubject<any>({});
  currentStaff$ = new BehaviorSubject<any>({});
  selectedStaff$ = new BehaviorSubject<any>({});
  allStaffs$ = new BehaviorSubject<any>([]);
  updateBillOwnerForm: UntypedFormGroup = new UntypedFormGroup({});
  isClientRepresentative = new BehaviorSubject(true);
  claims: string[] = ['can_do_billing'];
  claim$: Observable<Claim[]> = new Observable();

  ngOnInit(): void {
    this.componentInit();
  }

  componentInit() {
    this.updateBillOwnerForm = this.fb.group({
      newRepresentative: ['', [Validators.required]],
    });
    this.isClientRepresentative.next(false);
    this.staffStoreService
      .getStaffById(this.invoiceData().sales_representative)
      .subscribe((staff: STAFFINTERFACE) => {
        this.currentStaff$.next(staff);
        this.selectedStaff$.next(staff);
        this.NewRepresentative.setValue(staff.id);
      });
  }

  confirm(event: Event) {
    let translation: any;
    this.translate
      .get([
        'BILL_OWNER_CHANGE.CHANGE_INFO',
        'common.BUTTONS.BUTTON-TEXT-YES',
        'common.BUTTONS.BUTTON-TEXT-NO',
      ])
      .subscribe((translations) => {
        translation = translations;
      });
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      key: 'billowner',
      message: translation['BILL_OWNER_CHANGE.CHANGE_INFO'],
      icon: 'fa fa-exclamation-triangle',
      acceptLabel: translation['common.BUTTONS.BUTTON-TEXT-YES'],
      rejectLabel: translation['common.BUTTONS.BUTTON-TEXT-NO'],
      accept: () => {
        const changeSalesRep: ChangeSalesRep = {} as ChangeSalesRep;
        changeSalesRep.sales_rep_id = this.NewRepresentative.value.id;
        changeSalesRep.chnage_for_payments_linked_to_invoice = true;
        this.ChangeBillOwner(changeSalesRep);
      },
      reject: () => {
        // const changeSalesRep: ChangeSalesRep = {} as ChangeSalesRep;
        // changeSalesRep.sales_rep_id = this.NewRepresentative.value;
        // changeSalesRep.chnage_for_payments_linked_to_invoice= false;
        // this.ChangeBillOwner(changeSalesRep);;
      },
    });
  }

  ChangeBillOwner(changeSalesRep: ChangeSalesRep) {
    this.invoiceService
      .ChangeSalesRep(
        this.invoiceData().tenant_id,
        this.invoiceData().client_id,
        this.invoiceData().id,
        changeSalesRep,
      )
      .subscribe({
        next: () => {
          this.translate
            .get('BILL_OWNER_CHANGE.MSG.SUCCESS')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
          this.closeDialogEvent(true);
        },
        error: (error: AppErrors) => {
          if (error instanceof NotFoundError) {
            this.translate
              .get('common.errors.SERVER_SIDE.SOMETHING_WRONG')
              .subscribe((translation) => {
                this.toastMessageService.showErrorMessage(translation);
              });
          } else {
            if (error.errorCode == 'alreadyexist') {
              this.translate
                .get('BILL_OWNER_CHANGE.MSG.ALREADY_EXIST')
                .subscribe((translation) => {
                  this.toastMessageService.showSuccess(translation);
                });
            } else {
              this.translate
                .get('common.errors.SERVER_SIDE.SOMETHING_WRONG')
                .subscribe((translation) => {
                  this.toastMessageService.showErrorMessage(translation);
                });
            }
          }
        },
      });
  }

  get NewRepresentative(): UntypedFormControl {
    return this.updateBillOwnerForm.get(
      'newRepresentative',
    ) as UntypedFormControl;
  }

  selectStaff(staff: any) {
    this.selectedStaff$.next(staff);
    this.NewRepresentative.setValue(staff.id);
  }

  closeDialogEvent(updated = false) {
    this.closeDialog.emit({ action: 'close' });
    this.closeDialog.emit(updated ? { action: 'updated' } : { action: '' });
  }
}
